import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownModule } from 'primeng/dropdown';
import { BaseSelectComponent } from '../base-select/base-select.component';
import { CommonModule } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';

export interface LicenseType {
  value: string;
  text: string;
}

@Component({
  selector: 'license-type-select',
  imports: [
    TranslateModule,
    DropdownModule,
    CommonModule,
    FormsModule
  ],
  template: `
    <!-- PrimeNG Dropdown per desktop -->
    <p-dropdown
      *ngIf="!isMobile"
      class="w-100"
      [placeholder]="placeholder | translate"
      [(ngModel)]="selectedDropdownValue"
      [options]="listItems"
      optionLabel="name"
      [showClear]="true"
      (onChange)="handleDropdownChange($event.value)"
      [ngClass]="{'has-value': hasValue, 'is-invalid': isInvalid}"

      [loading]="isLoading"
      [disabled]="disabled"
      [readonly]="isReadonly"
      [required]="isRequired"
      [dataKey]="'item.value'">
    </p-dropdown>

    <!-- Select nativo per mobile -->
    <select
      *ngIf="isMobile"
      class="form-control mobile-select w-100"
      [class.is-invalid]="isInvalid"
      [disabled]="disabled"
      (change)="onNativeSelectChange($event)">
      <option value="">{{ placeholder | translate }}</option>
      <option *ngFor="let option of listItems" [value]="option.item.value" [selected]="selectedDropdownValue?.item?.value === option.item.value">
        {{ option.name }}
      </option>
    </select>
  `,
  styleUrls: ['../base-select/base-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LicenseTypeSelectComponent),
      multi: true
    }
  ]
})
export class LicenseTypeSelectComponent extends BaseSelectComponent<LicenseType, void> implements OnInit {
  @Input() licenseType: string | null = null;
  @Input() disabled: boolean = false;
  @Input() isReadonly: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() isInvalid: boolean = false;

  selectedDropdownValue: { name: string, item: LicenseType } | null = null;

  private licenseTypes: LicenseType[] = [
    { value: 'A', text: 'A' },
    { value: 'B', text: 'B' },
    { value: 'C', text: 'C' },
    { value: 'AM', text: 'AM' },
    { value: 'A1', text: 'A1' },
    { value: 'A2', text: 'A2' },
    { value: 'B1', text: 'B1' },
    { value: 'BE', text: 'BE' },
    { value: 'C1', text: 'C1' },
    { value: 'C1E', text: 'C1E' },
    { value: 'CE', text: 'CE' },
    { value: 'D', text: 'D' },
    { value: 'D1', text: 'D1' },
    { value: 'D1E', text: 'D1E' },
    { value: 'DE', text: 'DE' }
  ];

  constructor(
    breakpointObserver: BreakpointObserver
  ) {
    super(breakpointObserver);
    this.placeholder = 'SELECT_LICENSE_TYPE';
  }

  override ngOnInit(): void {
    super.ngOnInit();
    
    if (this.licenseType) {
      this.fetchAndSetInitialLicenseType(this.licenseType);
    } else {
      this.getItems('');
    }
  }

  fetchAndSetInitialLicenseType(licenseTypeValue: string): void {
    // Prima carica tutti i dati
    this.getItems('');
    
    const foundLicenseType = this.licenseTypes.find(lt => lt.value === licenseTypeValue);
    
    if (foundLicenseType) {
      this.writeValue(foundLicenseType);
      
      const currentOptionInList = this.listItems.find(opt => opt.item.value === foundLicenseType.value);
      if (!currentOptionInList) {
        this.listItems = [{ name: foundLicenseType.text, item: foundLicenseType }, ...this.listItems.filter(opt => opt.item.value !== foundLicenseType.value)];
      }
    }
  }

  override onSelectItem(item: LicenseType): void {
    if (item) {
      const currentValue = this.selectedDropdownValue?.item?.value;
      if (currentValue !== item.value) {
        this.hasValue = true;
        this.selectedDropdownValue = { name: item.text, item: item };
        // Emetti solo il valore stringa al FormControl
        this.onChange(item.value);
        this.onSelectedItem.emit(item);
      }
    }
  }

  override onClearItem(): void {
    this.hasValue = false;
    this.selectedDropdownValue = null;
    this.onChange(null);
    this.onSelectedItem.emit(null as any);
  }

  override writeValue(obj: string | LicenseType): void {
    if (obj) {
      let licenseTypeToSet: LicenseType | null = null;
      
      // Se obj è una stringa (valore dal FormControl)
      if (typeof obj === 'string') {
        licenseTypeToSet = this.licenseTypes.find(lt => lt.value === obj) || null;
      } else {
        // Se obj è già un oggetto LicenseType
        licenseTypeToSet = obj;
      }
      
      if (licenseTypeToSet) {
        // Verifica se il valore è già quello corrente per evitare loop infiniti
        const currentValue = this.selectedDropdownValue?.item?.value;
        if (currentValue !== licenseTypeToSet.value) {
          this.hasValue = true;
          this.selectedDropdownValue = { name: licenseTypeToSet.text, item: licenseTypeToSet };
          // Non chiamare onChange qui per evitare loop infiniti
          this.onSelectedItem.emit(licenseTypeToSet);
        }
      }
    } else {
      if (this.hasValue || this.selectedDropdownValue) {
        this.hasValue = false;
        this.selectedDropdownValue = null;
        this.onSelectedItem.emit(null as any);
      }
    }
  }

  handleDropdownChange(selectedOptionFromDropdown: { name: string, item: LicenseType } | null): void {
    if (selectedOptionFromDropdown) {
      super.onSelectItem(selectedOptionFromDropdown.item);
    } else {
      super.onClearItem();
    }
  }

  override getItems(query: string): void {
    this.isLoading = true;
    
    // Filtra i tipi di patente in base alla query se necessario
    const filteredLicenseTypes = query 
      ? this.licenseTypes.filter(lt => 
          lt.value.toLowerCase().includes(query.toLowerCase()) || 
          lt.text.toLowerCase().includes(query.toLowerCase())
        )
      : this.licenseTypes;

    this.listItems = filteredLicenseTypes.map((licenseType) => ({ 
      name: licenseType.text, 
      item: licenseType 
    }));
    
    this.isLoading = false;
  }
} 